import React from 'react';
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  HomeOutlined,
  ImportExportOutlined,
  MailOutlineOutlined,
  PermIdentityOutlined,
  SearchOutlined,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useMatch, useResolvedPath } from 'react-router-dom';
import shortid from 'shortid';

import { ApplicationState } from '../store';
import { AuthState } from '../store/auth/types';
import { adminLinks } from '../pages/admin';

export interface SidebarLinkProps {
  sidebarOpen: boolean;
}

export interface LinkInput {
  label: string;
  Icon?: any;
  to?: string;
  onClick?: any;
  [key: string]: any;
}

export interface MenuLink<T = {}, S = {}> extends LinkInput {
  subLinks?: LinkInput[];
}

const anonymousLinks: MenuLink[] = [
  {
    label: 'Dashboard',
    Icon: HomeOutlined,
    to: '/dashboard',
  },
  {
    label: 'Browse',
    Icon: SearchOutlined,
    to: '/lps',
  },
];

const userLinks: MenuLink[] = [
  ...anonymousLinks,
  {
    label: 'Reports',
    Icon: MailOutlineOutlined,
    to: '/reports',
  },
  {
    label: 'Profile',
    Icon: PermIdentityOutlined,
    to: '/profile',
  },
  {
    label: 'Upload / Download',
    Icon: ImportExportOutlined,
    to: '/docs',
  },
];

const ListItemSidebar: React.FC<MenuLink & any> = ({
  label,
  to,
  Icon: IconComponent,
  subLinks,
  indent: level = 0,
  child = false,
  className,
  sidebarIsOpen = true,
  expanded = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const match = to ? useMatch(to) : null;
  const exactMatch = match && location.pathname === match.pattern.path;

   // Determine if any sublink is active
   const isSubLinkActive = subLinks?.some(subLink => subLink.to && location.pathname.startsWith(subLink.to));

  // Parent should be open if it has an active sublink or if the expanded prop is true
  const open = isSubLinkActive || exactMatch || expanded;
  const color = (exactMatch || expanded) ? 'primary' : isSubLinkActive ? 'secondary' : undefined;

  const handleClick = () => {
    if (to) {
      navigate(to);
    }
  };

  return (
    <List key={shortid()} disablePadding>
      <ListItemButton onClick={handleClick} selected={exactMatch} style={{ minWidth: 0 }}>
        {IconComponent && (
          <ListItemIcon>
            <IconComponent color={color} />
          </ListItemIcon>
        )}

        <ListItemText
          primary={<Typography color={color}>{label}</Typography>}
          style={{ marginLeft: IconComponent === undefined ? 20 : 0 }}
        />
      </ListItemButton>

      <Collapse in={open && sidebarIsOpen}>
        {subLinks?.map(subLink => (
          <ListItemSidebar key={shortid()} {...subLink} indent={level + 1} child={true} />
        ))}
      </Collapse>
    </List>
  );
};

const SidebarLinks: React.FC<SidebarLinkProps> = ({ sidebarOpen }) => {
  const { data } = useSelector<ApplicationState, AuthState>(state => state.auth);
  let links = anonymousLinks;

  if (data?.UserType === 'Partner') links = userLinks;
  if (data?.Roles.includes('LPAdmin')) links = adminLinks;

  return (
    <>
      {links.map(props => (
        <ListItemSidebar key={shortid()} {...props} sidebarIsOpen={sidebarOpen} />
      ))}
    </>
  );
};

export default SidebarLinks;
